// Worklets: drag gestures place icons and hit-test the strip on the UI thread every frame.

/**
 * Where the editable strip sits. A docked strip (`bottom`, or a landscape `left`/`right` rail)
 * accepts a drop anywhere between it and the screen edge it is docked to; an inline preview
 * (`none`, on the Settings page itself) accepts drops only on or near its own box.
 */
export type PrimaryNavigationStripEditorEdge = "bottom" | "left" | "none" | "right";

/** Strip-local geometry, measured from the editor's own box. */
export type PrimaryNavigationStripEditorMetrics = {
  /** Icon center across the strip: from its top on a horizontal strip, its left on a rail. */
  crossCenter: number;
  edge: PrimaryNavigationStripEditorEdge;
  height: number;
  /** Padding before the first slot along the strip: left on a horizontal strip, top on a rail. */
  leadingInset: number;
  /** Padding after the last slot along the strip. */
  trailingInset: number;
  width: number;
};

export type PrimaryNavigationStripEditorPoint = { x: number; y: number };

/** How far a dragged icon may stray off the strip toward the page and still count as over it. */
export const PRIMARY_NAVIGATION_STRIP_EDITOR_HIT_SLOP = 24;

function isRail(metrics: PrimaryNavigationStripEditorMetrics): boolean {
  "worklet";
  return metrics.edge === "left" || metrics.edge === "right";
}

function getSlotLength(metrics: PrimaryNavigationStripEditorMetrics, slotCount: number): number {
  "worklet";
  const length =
    (isRail(metrics) ? metrics.height : metrics.width) -
    metrics.leadingInset -
    metrics.trailingInset;
  return Math.max(0, length) / Math.max(1, slotCount);
}

/**
 * Center of `slot` among `slotCount` evenly spaced slots. Matches the live strip, whose 44pt
 * buttons take equal auto margins (or shrink equally), so every button centers in an equal share.
 */
export function getPrimaryNavigationStripSlotCenter(
  metrics: PrimaryNavigationStripEditorMetrics,
  slot: number,
  slotCount: number,
): PrimaryNavigationStripEditorPoint {
  "worklet";
  const along = metrics.leadingInset + (slot + 0.5) * getSlotLength(metrics, slotCount);
  return isRail(metrics)
    ? { x: metrics.crossCenter, y: along }
    : { x: along, y: metrics.crossCenter };
}

function isOverPrimaryNavigationStrip(
  metrics: PrimaryNavigationStripEditorMetrics,
  point: PrimaryNavigationStripEditorPoint,
): boolean {
  "worklet";
  const slop = PRIMARY_NAVIGATION_STRIP_EDITOR_HIT_SLOP;
  switch (metrics.edge) {
    case "bottom": {
      return point.y >= -slop;
    }
    case "left": {
      return point.x <= metrics.width + slop;
    }
    case "right": {
      return point.x >= -slop;
    }
    case "none": {
      return (
        point.x >= -slop &&
        point.x <= metrics.width + slop &&
        point.y >= -slop &&
        point.y <= metrics.height + slop
      );
    }
  }
}

/** The slot under `point` (strip-local) among `slotCount` slots, or -1 when it is off the strip. */
export function getPrimaryNavigationStripHoverSlot(
  metrics: PrimaryNavigationStripEditorMetrics,
  point: PrimaryNavigationStripEditorPoint,
  slotCount: number,
): number {
  "worklet";
  if (slotCount <= 0 || !isOverPrimaryNavigationStrip(metrics, point)) {
    return -1;
  }
  const slotLength = getSlotLength(metrics, slotCount);
  if (slotLength <= 0) {
    return -1;
  }
  const along = (isRail(metrics) ? point.y : point.x) - metrics.leadingInset;
  return Math.max(0, Math.min(slotCount - 1, Math.floor(along / slotLength)));
}
